// src/components/ui/SessionWarningModal.tsx
import { Clock, LogOut, Shield } from 'lucide-react';

interface Props {
  secondsLeft: number;
  onStay: () => void;
  onLogout: () => void;
}

export function SessionWarningModal({ secondsLeft, onStay, onLogout }: Props) {
  const progress = Math.min(100, (secondsLeft / 60) * 100);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm animate-fade-up">
      <div className="card w-full max-w-md mx-4 p-8 shadow-floating animate-slide-in">
        {/* Icon */}
        <div className="flex items-center justify-center mb-6">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
            <Clock className="w-8 h-8 text-amber-600" strokeWidth={1.5} />
          </div>
        </div>

        {/* Content */}
        <div className="text-center mb-6">
          <h2 className="text-xl font-semibold text-slate-900 mb-2">
            Session Expiring Soon
          </h2>
          <p className="text-sm text-slate-500 leading-relaxed">
            You've been inactive for a while. For your security, you'll be automatically
            logged out in:
          </p>

          {/* Countdown */}
          <div className="mt-4 inline-flex items-center gap-2 px-6 py-3 bg-amber-50 rounded-full border border-amber-200">
            <span className="text-3xl font-bold font-mono text-amber-700 tabular-nums">
              {String(secondsLeft).padStart(2, '0')}
            </span>
            <span className="text-sm text-amber-600 font-medium">seconds</span>
          </div>

          {/* Progress bar */}
          <div className="mt-4 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-amber-400 rounded-full transition-all duration-1000 ease-linear"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onLogout}
            className="btn btn-secondary flex-1 text-slate-600"
          >
            <LogOut className="w-4 h-4" />
            Log out now
          </button>
          <button
            onClick={onStay}
            className="btn btn-primary flex-1"
            autoFocus
          >
            <Shield className="w-4 h-4" />
            Stay signed in
          </button>
        </div>
      </div>
    </div>
  );
}
